import ModalBox from "components/Modal";
import StyledButton from "components/shared/StyledButton";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import DocoonSendOptionsFields, {
    type DocoonSendOptionsValue,
    getDefaultDocoonSendOptions,
    isDocoonSendOptionsComplete,
    toDocoonSendOptionsPayload,
} from "./DocoonSendOptionsFields";

interface DocoonSendOptionsModalProps {
    open: boolean;
    isLoading: boolean;
    forceSequential?: boolean;
    onClose: () => void;
    onConfirm: (options: {
        signatureTemplate?: string;
        signSequential: number;
        serviceEdc?: string;
        typeDocEdc?: string;
        comment?: string;
    }) => void;
}

const DocoonSendOptionsModal = ({
    open,
    isLoading,
    forceSequential,
    onClose,
    onConfirm,
}: DocoonSendOptionsModalProps) => {
    const [t] = useTranslation();
    const { getCompanySettings } = useCompanySettings();
    const signSequentialDefault = !!getCompanySettings("docoon_sign_sequential_default");
    const [value, setValue] = useState<DocoonSendOptionsValue>(() =>
        getDefaultDocoonSendOptions(signSequentialDefault),
    );

    // The modal stays mounted with its parent page: re-apply defaults on each opening so the
    // latest company setting is used, even if settings were not loaded yet at mount time.
    useEffect(() => {
        if (open) setValue(getDefaultDocoonSendOptions(signSequentialDefault));
    }, [open, signSequentialDefault]);

    return (
        <ModalBox
            open={open}
            onClose={onClose}
            mainTitle={t("DocoonSignModal.ModalTitle")}
            subTitle={t("DocoonSignModal.ModalSubtitle")}
            style={{ width: 520, maxWidth: "95vw" }}
        >
            <div className="flex flex-col gap-5">
                <DocoonSendOptionsFields value={value} onChange={setValue} forceSequential={forceSequential} />

                <div className="h-px bg-[#ececf0]" />
                <div className="flex justify-end gap-2.5">
                    <StyledButton buttonType="secondary" onClick={onClose}>
                        {t("DocoonSignModal.Cancel")}
                    </StyledButton>
                    <StyledButton
                        buttonType="primary"
                        onClick={() => onConfirm(toDocoonSendOptionsPayload(value, forceSequential))}
                        loading={isLoading}
                        disabled={!isDocoonSendOptionsComplete(value)}
                    >
                        {t("DocoonSignModal.ConfirmSend")}
                    </StyledButton>
                </div>
            </div>
        </ModalBox>
    );
};

export default DocoonSendOptionsModal;
